<script setup lang="ts">

import {useCurrentWorkspaceUser} from "~/stores/spaces";
import {changePlanOfWorkspace} from "~/calls/subscription";
import {PRO_PLAN_TYPE} from "~/types/subscription.types";

const {isOwner, workspaceOwnerEmail} = useCurrentWorkspaceUser()
const {workspaceId, environmentId} = useEnvironment()
const {plan, currency} = useSubscriptionForEnvironment()
const router = useRouter();

const connections = ref(plan.value?.connections || 5)
const loading = ref(false)

watch([plan], () => {
  if (plan.value) {
    connections.value = plan.value.connections
  }
})

const hasFulfilledPlan = computed(() => {
  return plan.value && plan.value?.status !== 'PENDING_ACTIVATION'
})

const updatePlan = () => {
  loading.value = true
  const isDowngrade = plan.value && plan.value.connections > connections.value
  // It's an update
  changePlanOfWorkspace(
      workspaceId.value,
      environmentId.value,
      PRO_PLAN_TYPE,
      connections.value
  ).then((r) => {
    loading.value = false
    if (!r.success) return
    router.push(`/workspace/${workspaceId.value}/env/${environmentId.value}/subscription?action=${isDowngrade ? 'downgrade' : 'change'}&plan=Pro&connections=${connections.value}`)
  }).finally(() => {
    loading.value = false
  })
}

</script>
<template>
  <div v-if="plan">
    <p>
      You currently have a subscription to the AxonIQ Console Professional plan with {{ plan?.connections }}
      connections.
    </p>

    <div v-if="plan?.dailyUpdatesLeft !== undefined" class="my-4">
      <p>The changes to the subscription are limited to {{ plan.dailyUpdatesLimit }} times a day.</p>
      <div v-if="plan.dailyUpdatesLeft !== 0">
        <p>You can still change your subscription {{ plan.dailyUpdatesLeft }} times today.</p>
      </div>
    </div>

    <v-alert v-if="!isOwner" type="info" class="my-4">
      Only the owner of the workspace, {{ workspaceOwnerEmail }} can change the subscription.
    </v-alert>

    <div class="my-4">
      <p class="h5">
        How many connections do you need?
      </p>
      <SubscriptionFormProConnectionChooser v-model="connections" :currency="currency" :plan-type="PRO_PLAN_TYPE"
                                            class="mt-2"/>
    </div>
    <div>
      <div style="height: 130px">
        <div v-if="plan.connections < connections && plan.dailyUpdatesLeft != 0">
          <v-alert color="lightprimary">
            <p>
              Your new plan will have more connections than your current plan. Your current payment method will be
              charged
              immediately for the remaining days of your current subscription cycle.
            </p>
          </v-alert>
        </div>
        <div v-if="plan.connections > connections && plan?.dailyUpdatesLeft != 0">
          <v-alert color="lightprimary">
            <p>
              Your new plan will have less connections than your current plan.
              Your workspace will be credited with the remaining days of your current subscription cycle and will be
              used for following invoices automatically.
            </p>
          </v-alert>
        </div>
        <div v-if="plan.dailyUpdatesLeft == 0">
          <v-alert type="info">
            <p>
              The daily limit for updating the number of connections has been reached. You can change your subscription
              again tomorrow.
            </p>
          </v-alert>
        </div>
      </div>

      <div class="d-flex justify-end">
        <v-btn color="primary" @click="updatePlan" :loading="loading"
               :disabled="!isOwner || plan.connections == connections || plan.dailyUpdatesLeft == 0">
          {{ hasFulfilledPlan ? 'Update' : 'Start' }} your Subscription
        </v-btn>
      </div>
    </div>
  </div>
</template>
